import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField } from '@angular/forms/signals';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SearchConfig } from '../../../modules/variant-page/modules/variant-page/models';
import { RootSearchStore, RootUtilityStore } from '../../../store';
import { QuestionnaireComponent } from '../../containers/questionnaire/questionnaire.component';
import { ClickCursorDirective } from '../../directives/click-cursor.directive';
import { I18nPipe } from '../../i18n/i18n.pipe';
import { RtlDirective } from '../../i18n/rtl.directive';
import { DotsLoaderComponent } from '../dots-loader/dots-loader.component';

@Component({
  selector: 'gnx-search-bar',
  templateUrl: './search-bar.component.html',
  styleUrls: ['./search-bar.component.scss'],
  imports: [
    RtlDirective,
    ClickCursorDirective,
    DotsLoaderComponent,
    FormField,
    MatSelect,
    MatOption,
    QuestionnaireComponent,
    I18nPipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SearchBarComponent implements AfterViewInit {
  private router = inject(Router);

  readonly questionnaireRef = viewChild<QuestionnaireComponent>('questionnaire');

  protected searchStore = inject(RootSearchStore);

  private destroyRef = inject(DestroyRef);
  searchContext: SearchConfig;
  private utilityStore = inject(RootUtilityStore);

  private searchModel = signal({ search: '' });
  protected searchForm = form(this.searchModel);

  constructor() {
    const term = this.searchStore.term();
    if (term) {
      this.searchModel.set({ search: term });
    }

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.searchContext = this.searchContext?.zygosity === 'TUMOR' ? { zygosity: 'TUMOR' } : null;
        this.questionnaireRef()?.onSearchTermTyped('');
        this.searchForm.search().reset();
      });
  }

  ngAfterViewInit() {
    this.questionnaireRef().setIsTumor(this.isTumor);
  }

  protected get searchValue(): string {
    return this.searchForm.search().value();
  }

  get hgValue(): string {
    return localStorage.getItem('lastHGValue') || 'hg19';
  }

  set hgValue(value: string) {
    localStorage.setItem('lastHGValue', value);
    if (!this.searchForm.search().dirty()) {
      this.onSearch();
    }
  }

  get isTumor(): boolean {
    return localStorage.getItem('isTumorSearch') === 'true';
  }

  set isTumor(value: boolean) {
    localStorage.setItem('isTumorSearch', `${value}`);
    this.questionnaireRef().setIsTumor(value);
    if (!this.searchForm.search().dirty()) {
      this.onSearch();
    }
  }

  onUserInput(value: string) {
    this.questionnaireRef()?.onSearchTermTyped(value);
  }

  onSearch(redirect = true) {
    if (this.searchValue) {
      setTimeout(() => {
        this.utilityStore.searchVariant(this.searchValue, redirect, this.hgValue, {
          ...this.searchContext,
          search_term: this.searchValue,
        });
      });
    } else {
      this.onEscape();
    }
  }

  onEscape() {
    this.searchModel.set({ search: '' });
    this.searchContext = this.searchContext?.zygosity === 'TUMOR' ? { zygosity: 'TUMOR' } : null;
    this.searchStore.clearSearchError();
  }

  onContextChange(context: SearchConfig) {
    this.searchContext = context;
  }

  onPaste() {
    setTimeout(() => {
      this.searchForm.search().markAsDirty();
      this.onUserInput(this.searchValue);
    });
  }
}
